import { MemberAvatar } from '@/components/projects/MemberAvatar'
import { formatDateTimeLong } from '@/lib/timeFormatters'
import type { EntityHistoryEntry } from '@/types'
import {
  actorName,
  describeEntry,
  displayNewValue,
  displayOldValue,
  fieldLabel,
  type EntityHistoryFieldLabels,
} from './entityHistoryText'

interface EntityHistoryDetailProps {
  entry: EntityHistoryEntry
  entityLabel: string
  fieldLabels: EntityHistoryFieldLabels
}

export function EntityHistoryDetail({ entry, entityLabel, fieldLabels }: EntityHistoryDetailProps) {
  return (
    <div className="space-y-4" data-testid="entity-history-detail">
      <div className="flex items-center gap-3">
        <MemberAvatar name={actorName(entry)} className="h-7 w-7 shrink-0" />

        <div className="min-w-0">
          <p className="text-sm">
            <span className="font-semibold">{actorName(entry)}</span>{' '}
            <span className="text-muted-foreground">
              {describeEntry(entry, entityLabel, fieldLabels)}
            </span>
          </p>
          <p className="text-xs text-muted-foreground" data-testid="entity-history-detail-timestamp">
            {formatDateTimeLong(entry.performedAt)}
          </p>
        </div>
      </div>

      {entry.changes.length > 0 && (
        <dl className="space-y-3">
          {entry.changes.map(change => (
            <div key={change.fieldName} className="space-y-1" data-testid="entity-history-field-change">
              <dt className="text-xs font-semibold">{fieldLabel(change.fieldName, fieldLabels)}</dt>
              <dd className="flex flex-wrap items-center gap-2 text-sm">
                <span className="sr-only">Previous value:</span>
                <span
                  className="rounded-sm bg-danger px-1.5 py-0.5 text-danger-foreground line-through break-all"
                  data-testid="entity-history-old-value"
                >
                  {displayOldValue(change)}
                </span>
                <span className="sr-only">New value:</span>
                <span
                  className="rounded-sm bg-success px-1.5 py-0.5 text-success-foreground break-all"
                  data-testid="entity-history-new-value"
                >
                  {displayNewValue(change)}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
